import { Card, Row, Col } from 'react-bootstrap';
import {
  FuelStrategyResponse,
  RouteAnalysisResponse,
  StrategyOptions,
} from '../../models';
import { useLocaleContext } from '../../i18n';
import FuelStats from './FuelStats';
import PlannerNavigation from './PlannerNavigation';

interface Props {
  strategy: FuelStrategyResponse;
  route: RouteAnalysisResponse;
  strategyOptions: StrategyOptions;
  totalDuration: string;
  onBack: () => void;
  onGenerateSticker?: () => void;
  stickerLoading?: boolean;
}

export default function FuelPlanSummary({
  strategy,
  route,
  strategyOptions,
  totalDuration,
  onBack,
  onGenerateSticker,
  stickerLoading,
}: Props) {
  const { locale } = useLocaleContext();
  const durationMatch = totalDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  const hours = parseInt(durationMatch?.[1] ?? '0');
  const minutes = parseInt(durationMatch?.[2] ?? '0');

  const aggregateProducts = () => {
    const aggregate: Record<string, number> = {};
    strategy.points.forEach(p => {
      p.products.forEach(prod => {
        aggregate[prod.name] = (aggregate[prod.name] ?? 0) + prod.quantity;
      });
    });
    return Object.entries(aggregate)
      .map(([name, qty]) => ({ name, qty }))
      .sort((a, b) => b.qty - a.qty);
  };

  const totalHours = hours + minutes / 60 || 1;
  const avgCarbsPerHour = Math.round(strategy.totalCarbs / totalHours);

  return (
    <div className="d-flex flex-column" style={{ gap: '0.25rem' }}>
      <FuelStats
        totalCarbs={strategy.totalCarbs}
        totalConsumedKcal={strategy.totalCarbs * 4}
        totalBalance={strategy.totalCarbs * 4 - strategy.points.reduce((s, p) => s + p.burnedKcal, 0)}
        avgCarbsPerHour={avgCarbsPerHour}
        strategyOptions={strategyOptions}
        totalDuration={totalDuration}
      />

      {/* Route Overview */}
      <Card>
        <Card.Header><Card.Title className="mb-0">{locale.planner.summary.route}</Card.Title></Card.Header>
        <Card.Body>
          <Row>
            <Col sm={3}>
              <div className="text-secondary">{locale.planner.summary.distance}</div>
              <div className="h5 text-light mb-0">{route.totalDistanceKm.toFixed(1)} km</div>
            </Col>
            <Col sm={3}>
              <div className="text-secondary">{locale.planner.summary.duration}</div>
              <div className="h5 text-light mb-0">
                {hours}:{String(minutes).padStart(2, '0')}
              </div>
            </Col>
            <Col sm={2}>
              <div className="text-secondary">{locale.planner.summary.elevationGain}</div>
              <div className="h5 text-light mb-0">{Math.round(route.totalElevationGainM)} m</div>
            </Col>
            <Col sm={2}>
              <div className="text-secondary">{locale.planner.summary.elevationLoss}</div>
              <div className="h5 text-light mb-0">{Math.round(route.totalElevationLossM)} m</div>
            </Col>
            <Col sm={2}>
              <div className="text-secondary">{locale.planner.summary.fuelPoints}</div>
              <div className="h5 text-light mb-0">{strategy.points.length}</div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Packing List */}
      <Card>
        <Card.Header><Card.Title className="mb-0">{locale.planner.summary.packingList}</Card.Title></Card.Header>
        <Card.Body>
          <div className="d-flex flex-column gap-2">
            {aggregateProducts().map(({ name, qty }) => (
              <div key={name} className="d-flex justify-content-between align-items-center">
                <span className="text-light">{Math.ceil(qty)}x {name}</span>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>

      {/* Cyclist Profile */}
      <Card>
        <Card.Header><Card.Title className="mb-0">{locale.planner.summary.cyclistProfile}</Card.Title></Card.Header>
        <Card.Body>
          <Row>
            <Col sm={4}>
              <div className="text-secondary">{locale.planner.summary.ftp}</div>
              <div className="h5 text-light mb-0">{strategyOptions.cyclist.ftpWatts} W</div>
            </Col>
            <Col sm={4}>
              <div className="text-secondary">{locale.planner.summary.weight}</div>
              <div className="h5 text-light mb-0">{strategyOptions.cyclist.weightKg} kg</div>
            </Col>
            <Col sm={4}>
              <div className="text-secondary">{locale.planner.summary.zone}</div>
              <div className="h5 text-light mb-0">{strategyOptions.targetZone}</div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Strategy */}
      <Card>
        <Card.Header><Card.Title className="mb-0">{locale.planner.summary.strategy}</Card.Title></Card.Header>
        <Card.Body>
          <Row>
            <Col sm={3}>
              <div className="text-secondary">{locale.planner.summary.refuelEvery}</div>
              <div className="h5 text-light mb-0">{strategyOptions.frequencyMinutes} min</div>
            </Col>
            <Col sm={3}>
              <div className="text-secondary">{locale.planner.summary.maxAbsorption}</div>
              <div className="h5 text-light mb-0">{strategyOptions.maxAbsorptionRatePerHour} g/h</div>
            </Col>
            <Col sm={3}>
              <div className="text-secondary">{locale.planner.summary.maxSurplus}</div>
              <div className="h5 text-light mb-0">{strategyOptions.maxSurplusGramsPerInterval} g</div>
            </Col>
            <Col sm={3}>
              <div className="text-secondary">{locale.planner.summary.maxDeficit}</div>
              <div className="h5 text-light mb-0">{strategyOptions.maxDeficitGramsPerInterval} g</div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <PlannerNavigation onBack={onBack} onAction={onGenerateSticker} actionLabel={locale.planner.summary.generateSticker} loading={stickerLoading} />
    </div>
  );
}
